unit Essentials.RequiredFields.UtilityUnit;

interface

uses Generics.Collections, WEbLib.Forms, System.Classes,
{$IFDEF PAS2JS}
  WEBLib.Controls,
{$ELSE}
  VCL.Controls,
{$ENDIF}
  WEBLib.Dialogs;

type
  /// <summary>
  /// Record structure to hold information about a missing required field.
  /// Used to track which fields are missing during validation and to generate user-friendly error messages.
  /// </summary>
  TMissingFieldInfo = record
    Name: string;              // Control name (e.g., 'Edit1')
    ElementID: string;         // HTML element ID for DOM manipulation
    DataField: string;         // Database/entity field name (e.g., 'DonorName')
    EntityClassName: string;   // Entity class name (e.g., 'TDonation')
  end;

TRequiredFieldsUtils = class
private
  class function GetPropertyValueAsString(AControl: TObject; const APropertyName: string; out AValue: string): Boolean;
  class function GetPropertyValueAsBoolean(AControl: TObject; const APropertyName: string; out AValue: Boolean): Boolean;
  class function GetPropertyValueAsInteger(AControl: TObject; const APropertyName: string; out AValue: Integer): Boolean;
  class function GetPropertyValueAsDouble(AControl: TObject; const APropertyName: string; out AValue: Double): Boolean;
  class function HasControlValue(AControl: TObject): Boolean;
  class function IsControlRequired(AControl: TControl): Boolean;
  class function GetLabelText(const AElementID: string): string;
  class procedure SetLabelText(const AElementID: string; const AText: string);
  class procedure AppendRequiredIndicator(const AElementID: string);
  class procedure UpdateControlValidity(AControl: TObject);
  class function ValidateRequiredField(AControl: TControl; out AMissingFieldInfo: TMissingFieldInfo): Boolean;
public
  class function HasDataAwareProperties(AControl: TObject; out ADataField: string; out AEntitySetName: string): Boolean;
  class function ValidateRequiredFields(aForm: TForm; aMissingFields: TList<TMissingFieldInfo>): Boolean;
  class function CreateMissingFieldsMessage(const aMissingFields: TList<TMissingFieldInfo>): string;
  class function ValidateAndShowMessage(aForm: TForm): Boolean;
  class procedure MarkRequiredFieldLabels(aForm: TForm; const aEntityClassName: string);
  class procedure SetupRequiredFieldValidity(aForm: TForm);
  class procedure UpdateRequiredFieldsValidity(aForm: TForm);
  class procedure MarkRequiredFieldLabel(AControl: TControl; const aEntityClassName: string);
  class procedure SetupRequiredFieldValidityForControl(AControl: TControl);
end;

implementation

uses System.RTTI, SysUtils, Web, Essentials.Web.Shared, Essentials.Web.Manager, SMX.Web.Document.Utils, WEBLib.StdCtrls, WEBLib.DBCtrls, SMx.Web.Utils;

/// <summary>
/// Retrieves a property value as a string from a control using RTTI (Runtime Type Information).
/// This is a generic helper method that allows accessing properties without knowing the exact control type at compile time.
/// </summary>
/// <param name="AControl">The control object to inspect</param>
/// <param name="APropertyName">The name of the property to retrieve (e.g., 'ElementID', 'DataField')</param>
/// <param name="AValue">Output parameter containing the property value as a string, or empty string if not found</param>
/// <returns>True if the property exists and was successfully retrieved, False otherwise</returns>
class function TRequiredFieldsUtils.GetPropertyValueAsString(AControl: TObject; const APropertyName: string; out AValue: string): Boolean;
var
  RttiContext: TRttiContext;
  RttiType: TRttiType;
  Prop: TRttiProperty;
begin
  Result := False;
  AValue := '';
  RttiContext := TRttiContext.Create;
  try
    RttiType := RttiContext.GetType(AControl.ClassType);
    if RttiType <> nil then
    begin
      Prop := RttiType.GetProperty(APropertyName);
      if Prop <> nil then
      begin
        try
          AValue := Prop.GetValue(AControl).AsString;
          Result := True;
        except
          AValue := '';
        end;
      end;
    end;
  finally
    RttiContext.Free;
  end;
end;

/// <summary>
/// Retrieves a property value as a Boolean from a control using RTTI.
/// Used primarily to check the 'Required' property of controls.
/// </summary>
/// <param name="AControl">The control object to inspect</param>
/// <param name="APropertyName">The name of the Boolean property to retrieve</param>
/// <param name="AValue">Output parameter containing the property value, or False if not found</param>
/// <returns>True if the property exists and was successfully retrieved, False otherwise</returns>
class function TRequiredFieldsUtils.GetPropertyValueAsBoolean(AControl: TObject; const APropertyName: string; out AValue: Boolean): Boolean;
var
  RttiContext: TRttiContext;
  RttiType: TRttiType;
  Prop: TRttiProperty;
begin
  Result := False;
  AValue := False;
  RttiContext := TRttiContext.Create;
  try
    RttiType := RttiContext.GetType(AControl.ClassType);
    if RttiType <> nil then
    begin
      Prop := RttiType.GetProperty(APropertyName);
      if Prop <> nil then
      begin
        try
          AValue := Prop.GetValue(AControl).AsBoolean;
          Result := True;
        except
          AValue := False;
        end;
      end;
    end;
  finally
    RttiContext.Free;
  end;
end;

/// <summary>
/// Retrieves a property value as an Integer from a control using RTTI.
/// Primarily used to check the 'ItemIndex' property of combo box controls (where -1 indicates no selection).
/// </summary>
/// <param name="AControl">The control object to inspect</param>
/// <param name="APropertyName">The name of the Integer property to retrieve</param>
/// <param name="AValue">Output parameter containing the property value, or -1 if not found</param>
/// <returns>True if the property exists and was successfully retrieved, False otherwise</returns>
class function TRequiredFieldsUtils.GetPropertyValueAsInteger(AControl: TObject; const APropertyName: string; out AValue: Integer): Boolean;
var
  RttiContext: TRttiContext;
  RttiType: TRttiType;
  Prop: TRttiProperty;
begin
  Result := False;
  AValue := -1;
  RttiContext := TRttiContext.Create;
  try
    RttiType := RttiContext.GetType(AControl.ClassType);
    if RttiType <> nil then
    begin
      Prop := RttiType.GetProperty(APropertyName);
      if Prop <> nil then
      begin
        try
          AValue := Prop.GetValue(AControl).AsInteger;
          Result := True;
        except
          AValue := -1;
        end;
      end;
    end;
  finally
    RttiContext.Free;
  end;
end;

class function TRequiredFieldsUtils.GetPropertyValueAsDouble(AControl: TObject; const APropertyName: string; out AValue: Double): Boolean;
var
  RttiContext: TRttiContext;
  RttiType: TRttiType;
  Prop: TRttiProperty;
begin
  Result := False;
  AValue := 0;
  RttiContext := TRttiContext.Create;
  try
    RttiType := RttiContext.GetType(AControl.ClassType);
    if RttiType <> nil then
    begin
      Prop := RttiType.GetProperty(APropertyName);
      if Prop <> nil then
      begin
        try
          AValue := Prop.GetValue(AControl).AsExtended;
          Result := True;
        except
          AValue := 0;
        end;
      end;
    end;
  finally
    RttiContext.Free;
  end;
end;

/// <summary>
/// Determines if a control has a non-empty value. This is the core validation logic for checking if required fields are filled.
/// Handles different control types:
/// - Combo boxes: Checks ItemIndex (-1 = invalid) and validates that selected item has non-blank text/value
/// - Text inputs: Checks Value or Text property for non-empty content
/// Uses a priority order: ItemIndex -> Value -> Text, to handle various control implementations.
/// </summary>
/// <param name="AControl">The control to check for a value</param>
/// <returns>True if the control has a non-empty value, False if it's empty or invalid</returns>
class function TRequiredFieldsUtils.HasControlValue(AControl: TObject): Boolean;
var
  StrValue: string;
  IntValue: Integer;
  DateValue: Double;
begin
  Result := False;
  
  // Check for "ItemIndex" property first (for combo boxes)
  if GetPropertyValueAsInteger(AControl, 'ItemIndex', IntValue) then
  begin
    // ItemIndex = -1 means nothing selected (invalid)
    if IntValue = -1 then
    begin
      Result := False;
      Exit;
    end;
    
    // If ItemIndex >= 0, check if the selected item's text/value is blank
    // First check Text property (which shows the selected item's display text)
    if GetPropertyValueAsString(AControl, 'Text', StrValue) then
    begin
      // If text is blank or empty, it's invalid
      if Trim(StrValue) = '' then
      begin
        Result := False;
        Exit;
      end
      else
      begin
        Result := True;
        Exit;
      end;
    end;
    
    // If no Text property, check Value property
    if GetPropertyValueAsString(AControl, 'Value', StrValue) then
    begin
      // If value is blank or empty, it's invalid
      if Trim(StrValue) = '' then
      begin
        Result := False;
        Exit;
      end
      else
      begin
        Result := True;
        Exit;
      end;
    end;
    
    // If ItemIndex >= 0 but we can't check Text/Value, assume it's valid
    // (ItemIndex 0 might be blank, but we can't determine that without Text/Value)
    Result := IntValue >= 0;
    Exit;
  end;

  // Date/time controls should validate by Date value, not by placeholder text.
  if GetPropertyValueAsDouble(AControl, 'Date', DateValue) then
  begin
    Result := DateValue > 0;
    Exit;
  end;
  
  // Check for "Value" property (for non-combo controls)
  if GetPropertyValueAsString(AControl, 'Value', StrValue) then
  begin
    if Trim(StrValue) <> '' then
    begin
      Result := True;
      Exit;
    end;
  end;
  
  // Check for "Text" property (for non-combo controls)
  if GetPropertyValueAsString(AControl, 'Text', StrValue) then
  begin
    StrValue := Trim(LowerCase(StrValue));
    if (StrValue = 'dd/mm/yyyy') or (StrValue = 'mm/dd/yyyy') then
      Exit(False);

    if Trim(StrValue) <> '' then
    begin
      Result := True;
      Exit;
    end;
  end;
end;

/// <summary>
/// Checks if a control has data-aware properties (DataField, Required, DataSource) and extracts the DataField and EntitySetName.
/// This identifies controls that are bound to database entities, allowing the system to determine which entity class they belong to.
/// Navigates the DataSource -> DataSet -> EntitySetName chain using RTTI to extract the entity information.
/// </summary>
/// <param name="AControl">The control to inspect</param>
/// <param name="ADataField">Output parameter containing the DataField property value (e.g., 'DonorName')</param>
/// <param name="AEntitySetName">Output parameter containing the EntitySetName from DataSource.DataSet (e.g., 'Donation')</param>
/// <returns>True if the control has all three required properties (DataField, Required, DataSource), False otherwise</returns>
class function TRequiredFieldsUtils.HasDataAwareProperties(AControl: TObject; out ADataField: string; out AEntitySetName: string): Boolean;
var
  RttiContext: TRttiContext;
  RttiType: TRttiType;
  Prop: TRttiProperty;
  DataFieldProp, DataSourceProp: TRttiProperty;
  DataSourceValue: TValue;
  DataSourceObj, DataSetObj: TObject;
  DataSetProp, EntitySetNameProp: TRttiProperty;
begin
  Result := False;
  ADataField := '';
  AEntitySetName := '';
  DataFieldProp := nil;
  DataSourceProp := nil;

  RttiContext := TRttiContext.Create;
  try
    RttiType := RttiContext.GetType(AControl.ClassType);
    if RttiType <> nil then
    begin
      // Find all three properties
      for Prop in RttiType.GetProperties do
      begin
        if SameText(Prop.Name, 'DataField') then
          DataFieldProp := Prop
        else if SameText(Prop.Name, 'DataSource') then
          DataSourceProp := Prop;
      end;

      // Data-aware controls are identified by DataField + DataSource.
      // Don't require a "Required" property, as some controls (e.g. date pickers)
      // might not expose it and would be skipped from required-field setup.
      if (DataFieldProp <> nil) and (DataSourceProp <> nil) then
      begin
        // Extract DataField value using helper
        GetPropertyValueAsString(AControl, 'DataField', ADataField);

        // Extract EntitySetName from DataSource.DataSet.EntitySetName
        try
          DataSourceValue := DataSourceProp.GetValue(AControl);
          if not DataSourceValue.IsEmpty and DataSourceValue.IsObject then
          begin
            DataSourceObj := DataSourceValue.AsObject;
            if DataSourceObj <> nil then
            begin
              // Get DataSet property from DataSource
              RttiType := RttiContext.GetType(DataSourceObj.ClassType);
              DataSetProp := RttiType.GetProperty('DataSet');
              if DataSetProp <> nil then
              begin
                DataSourceValue := DataSetProp.GetValue(DataSourceObj);
                if not DataSourceValue.IsEmpty and DataSourceValue.IsObject then
                begin
                  DataSetObj := DataSourceValue.AsObject;
                  // Get EntitySetName property from DataSet
                  RttiType := RttiContext.GetType(DataSetObj.ClassType);
                  EntitySetNameProp := RttiType.GetProperty('EntitySetName');
                  if EntitySetNameProp <> nil then
                    AEntitySetName := EntitySetNameProp.GetValue(DataSetObj).AsString;
                end;
              end;
            end;
          end;
        except
          AEntitySetName := '';
        end;

        Result := True;
      end;
    end;
  finally
    RttiContext.Free;
  end;
end;


class function TRequiredFieldsUtils.IsControlRequired(AControl: TControl): Boolean;
var
  dataField, entitySetName: string;
  nonDataAwareEntityClassName, nonDataAwareFieldName: string;
begin
  Result := False;

  if HasDataAwareProperties(AControl, dataField, entitySetName) then
  begin
    if (entitySetName <> '') and (dataField <> '') then
    begin
      EssentialsManager.GetMappedEntitySetName(entitySetName, dataField, entitySetName);
      Result := EssentialsManager.RequiredFieldsManager.IsFieldRequired('T' + entitySetName, dataField);
      Exit;
    end;
  end;

  if EssentialsManager.RequiredFieldsManager.GetNonDataAwareMapping(
    AControl, {o}nonDataAwareEntityClassName, {o}nonDataAwareFieldName) then
  begin
    Result := EssentialsManager.RequiredFieldsManager.IsFieldRequired(
      nonDataAwareEntityClassName, nonDataAwareFieldName);
  end;
end;

/// <summary>
/// Validates a single control to determine if it's a required field that is missing a value.
/// If the field is required but empty, populates AMissingFieldInfo with details about the missing field.
/// Handles both data-aware controls (derives EntityClassName from DataSource) and non-data-aware controls
/// (looks up EntityClassName from the RequiredFieldsManager registry).
/// </summary>
/// <param name="AControl">The control to validate</param>
/// <param name="AMissingFieldInfo">Output parameter containing information about the missing field if validation fails</param>
/// <returns>True if the field is valid (either not required, or required and has a value), False if required and empty</returns>
class function TRequiredFieldsUtils.ValidateRequiredField(AControl: TControl; out AMissingFieldInfo: TMissingFieldInfo): Boolean;
var
  dataField, entitySetName: string;
  mapping: TLookupMapping;
  nonDataAwareEntityClassName, nonDataAwareFieldName: string;
begin
  Result := True; // Assume valid

  if IsControlRequired(AControl) then
  begin
    if not HasControlValue(AControl) then
    begin
      AMissingFieldInfo.Name := AControl.Name;
      GetPropertyValueAsString(AControl, 'ElementID', AMissingFieldInfo.ElementID);
      GetPropertyValueAsString(AControl, 'DataField', AMissingFieldInfo.DataField);

      // Derive EntityClassName from EntitySetName (e.g., 'Donation' -> 'TDonation')
      AMissingFieldInfo.EntityClassName := '';
      if HasDataAwareProperties(AControl, dataField, entitySetName) then
      begin
        // Data-aware control - derive from DataSource
        if entitySetName <> '' then
        begin
          // Use utility function to get mapped name (returns original if no mapping found)
          EssentialsManager.GetMappedEntitySetName(entitySetName, dataField, entitySetName);
          AMissingFieldInfo.EntityClassName := 'T' + entitySetName;
        end;
      end
      else
      begin
        // Non-data-aware control - check registry
        if EssentialsManager.RequiredFieldsManager.GetNonDataAwareMapping(
          AControl, {o}nonDataAwareEntityClassName, {o}nonDataAwareFieldName) then
        begin
          AMissingFieldInfo.EntityClassName := nonDataAwareEntityClassName;
          AMissingFieldInfo.DataField := nonDataAwareFieldName;
        end;
      end;

      Result := False;
    end;
  end;
end;

/// <summary>
/// Validates all required fields on a form by iterating through all controls and checking each one.
/// Collects all missing required fields into a list for reporting purposes.
/// </summary>
/// <param name="aForm">The form containing controls to validate</param>
/// <param name="aMissingFields">Output list that will be populated with information about all missing required fields</param>
/// <returns>True if all required fields have values, False if any required fields are missing</returns>
class function TRequiredFieldsUtils.ValidateRequiredFields(aForm: TForm; aMissingFields: TList<TMissingFieldInfo>): Boolean;
var
  I: Integer;
  Ctrl: TControl;
  MissingFieldInfo: TMissingFieldInfo;
  dataField, entitySetName: string;
  mapping: TLookupMapping;
begin
  aMissingFields.Clear;
  for I := 0 to aForm.ControlCount - 1 do
    if not ValidateRequiredField(aForm.Controls[I], MissingFieldInfo) then
      aMissingFields.Add(MissingFieldInfo);

  Result := aMissingFields.Count = 0;
end;

class function TRequiredFieldsUtils.GetLabelText(const AElementID: string): string;
begin
  Result := '';
  {$IFDEF PAS2JS}
  asm
    var labelEl = document.getElementById(AElementID);
    if (labelEl !== null) {
      // Get text content and remove any asterisks or SVG-related text
      var text = labelEl.textContent || labelEl.innerText || '';
      // Remove asterisks and trim whitespace
      text = text.replace(/\s*\*\s*/g, '').trim();
      // Also check for visually-hidden "required" text and remove it
      var hiddenDivs = labelEl.querySelectorAll('.visually-hidden');
      if (hiddenDivs.length > 0) {
        // Clone the element to work with text without hidden elements
        var clone = labelEl.cloneNode(true);
        var hidden = clone.querySelectorAll('.visually-hidden');
        for (var i = 0; i < hidden.length; i++) {
          hidden[i].remove();
        }
        var svg = clone.querySelectorAll('svg.ricon');
        for (var i = 0; i < svg.length; i++) {
          svg[i].remove();
        }
        text = clone.textContent || clone.innerText || '';
        text = text.trim();
      }
      Result = text;
    }
  end;
  {$ENDIF}
end;

/// <summary>
/// Sets the text content of a label element in the DOM by its ElementID.
/// Uses JavaScript DOM manipulation (PAS2JS compilation target).
/// </summary>
/// <param name="AElementID">The HTML element ID of the label to update</param>
/// <param name="AText">The new text content to set</param>
class procedure TRequiredFieldsUtils.SetLabelText(const AElementID: string; const AText: string);
begin
  {$IFDEF PAS2JS}
  asm
    var labelEl = document.getElementById(AElementID);
    if (labelEl !== null) {
      labelEl.textContent = AText;
    }
  end;
  {$ENDIF}
end;

class procedure TRequiredFieldsUtils.AppendRequiredIndicator(const AElementID: string);
begin
  {$IFDEF PAS2JS}
  asm
    var labelEl = document.getElementById(AElementID);
    if (labelEl !== null) {
      // Check if required indicator already exists
      var hasRequired = labelEl.querySelector('svg.ricon') !== null;
      if (!hasRequired) {
        // Add a space before the SVG indicator
        var space = document.createTextNode(' ');
        labelEl.appendChild(space);
        
        // Append SVG required indicator
        var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        svg.setAttribute('class', 'ricon');
        svg.setAttribute('focusable', 'false');
        var use = document.createElementNS('http://www.w3.org/2000/svg', 'use');
        use.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', '#required');
        svg.appendChild(use);
        labelEl.appendChild(svg);
        
        // Add visually-hidden div for accessibility
        var hiddenDiv = document.createElement('div');
        hiddenDiv.setAttribute('class', 'visually-hidden');
        hiddenDiv.textContent = 'required';
        labelEl.appendChild(hiddenDiv);
      }
    }
  end;
  {$ENDIF}
end;

/// <summary>
/// Creates a user-friendly error message listing all missing required fields.
/// Attempts to retrieve human-readable field names by:
/// 1. Looking up label text from the DOM using the DataField pattern (entity.field.label)
/// 2. Falling back to ElementID pattern if DataField lookup fails
/// 3. Using custom field names from CustomNamesManager if available
/// 4. Finally falling back to the control name or DataField name
/// Formats entity class names by removing the 'T' prefix and converting to lowercase.
/// </summary>
/// <param name="aMissingFields">List of missing field information to include in the message</param>
/// <returns>A formatted string message listing all missing fields, or empty string if list is empty</returns>
class function TRequiredFieldsUtils.CreateMissingFieldsMessage(const aMissingFields: TList<TMissingFieldInfo>): string;
  function FormatEntityClassName(aEntityClassName: string): string;
  begin
    Result := aEntityClassName;
    if (Length(aEntityClassName) > 0) and (aEntityClassName[1] = 'T') then
      Result := Copy(Result, 2, MaxInt);
    Result := LowerCase(Result);
  end;
var
  I: Integer;
  LabelText: string;
  LabelElementID: string;
  CustomName: string;
  DisplayName: string;
  EntityNameLower: string;
  EntityClassName: string;
begin
  Result := '';
  
  if (aMissingFields = nil) or (aMissingFields.Count = 0) then
    Exit;
  
  Result := 'Please complete the following required fields:<br><br>';
  
  for I := 0 to aMissingFields.Count - 1 do
  begin
    EntityClassName := aMissingFields[I].EntityClassName;
    EntityNameLower := FormatEntityClassName(EntityClassName);

    // Try to find label text from HTML
    LabelText := '';
    if aMissingFields[I].DataField <> '' then
    begin
      LabelElementID := EntityNameLower + '.' + LowerCase(aMissingFields[I].DataField) + '.label';
      LabelText := GetLabelText(LabelElementID);
    end;
    
    // If label not found by DataField pattern, try using ElementID from control
    if (LabelText = '') and (aMissingFields[I].ElementID <> '') then
    begin
      LabelElementID := LowerCase(aMissingFields[I].ElementID) + '.label';
      LabelText := GetLabelText(LabelElementID);
    end;

    if LabelText <> '' then
    begin
      Result := Result + '• ' + LabelText;
    end
    else
    begin
      // Try to get custom name for the DataField (only if label was not found)
      if (aMissingFields[I].DataField <> '') and (EntityClassName <> '') then
      begin
        try
          CustomName := EssentialsManager.CustomNamesManager.EntityFieldName(EntityClassName, aMissingFields[I].DataField);
        except
          CustomName := '';
        end;
        
        if CustomName <> '' then
          DisplayName := CustomName
        else
          DisplayName := TUtils.SplitOnCaps(aMissingFields[I].DataField);

        Result := Result + '• ' + DisplayName;
      end;
    end;
    
    Result := Result + '<br>';
  end;
end;

/// <summary>
/// Validates all required fields on a form and displays a message dialog if any are missing.
/// This is a convenience method that combines validation and user notification in a single call.
/// Uses TWebMessageDlg to display the message with only an OK button.
/// </summary>
/// <param name="aForm">The form to validate</param>
/// <returns>True if all required fields are valid, False if any are missing (and message was shown)</returns>
class function TRequiredFieldsUtils.ValidateAndShowMessage(aForm: TForm): Boolean;
var
  missingFields: TList<TMissingFieldInfo>;
  MessageText: string;
  MessageDlg: TMessageDlg;
begin
  Result := True;
  missingFields := TList<TMissingFieldInfo>.Create;
  try
    // Validate required fields (EntityClassName will be derived from controls, respecting mappings)
    if not ValidateRequiredFields(aForm, missingFields) then
    begin
      MessageText := CreateMissingFieldsMessage(missingFields);
      if MessageText <> '' then
      begin
        // Create and show TWebMessageDlg with only OK button
        // The dialog is created with aForm as parent, so the form manages its lifetime
        MessageDlg := TMessageDlg.Create(aForm);
        MessageDlg.ShowDialog(MessageText, mtWarning, [mbOK]);
      end;
      Result := False; // Validation failed
    end;
  finally
    missingFields.Free;
  end;
end;

/// <summary>
/// Marks a single control's associated label with a visual required field indicator (SVG icon).
/// Attempts to find the label using two patterns:
/// 1. DataField-based pattern: entity.field.label (e.g., 'donation.donorname.label')
/// 2. ElementID-based pattern: elementid.label (e.g., 'edit1.label')
/// Handles both data-aware controls (uses DataField property) and non-data-aware controls
/// (looks up mapping from RequiredFieldsManager registry).
/// Only marks labels for controls that have Required=True.
/// </summary>
/// <param name="AControl">The control whose label should be marked</param>
/// <param name="aEntityClassName">The entity class name to use for label lookup (e.g., 'TDonation')</param>
class procedure TRequiredFieldsUtils.MarkRequiredFieldLabel(AControl: TControl; const aEntityClassName: string);
var
  ElementID: string;
  DataField: string;
  LabelElementID: string;
  LabelText: string;
  EntityNameLower: string;
  nonDataAwareEntityClassName, nonDataAwareFieldName: string;
begin
  // Build entity name for label lookup (remove 'T' prefix and convert to lowercase)
  EntityNameLower := aEntityClassName;
  if (Length(EntityNameLower) > 0) and (EntityNameLower[1] = 'T') then
    EntityNameLower := Copy(EntityNameLower, 2, MaxInt);
  EntityNameLower := LowerCase(EntityNameLower);
  
  // Get ElementID
  ElementID := '';
  GetPropertyValueAsString(AControl, 'ElementID', ElementID);
  
  // Get DataField - try from control property first (data-aware), then from registry (non-data-aware)
  DataField := '';
  GetPropertyValueAsString(AControl, 'DataField', DataField);
  
  // If no DataField from property, check registry for non-data-aware controls
  if DataField = '' then
  begin
    if EssentialsManager.RequiredFieldsManager.GetNonDataAwareMapping(
      AControl, {o}nonDataAwareEntityClassName, {o}nonDataAwareFieldName) then
    begin
      DataField := nonDataAwareFieldName;
      // Use the entity class name from registry if provided, otherwise use parameter
      if nonDataAwareEntityClassName <> '' then
      begin
        EntityNameLower := nonDataAwareEntityClassName;
        if (Length(EntityNameLower) > 0) and (EntityNameLower[1] = 'T') then
          EntityNameLower := Copy(EntityNameLower, 2, MaxInt);
        EntityNameLower := LowerCase(EntityNameLower);
      end;
    end;
  end;
  
  // Try to find label using DataField pattern first
  LabelElementID := '';
  LabelText := '';
  if DataField <> '' then
  begin
    LabelElementID := EntityNameLower + '.' + LowerCase(DataField) + '.label';
    LabelText := GetLabelText(LabelElementID);
    
    // If label found, add required indicator (SVG)
    if LabelText <> '' then
    begin
      AppendRequiredIndicator(LabelElementID);
      Exit; // Done, move to next control
    end;
  end;
  
  // If not found by DataField pattern, try using ElementID
  if (LabelText = '') and (ElementID <> '') then
  begin
    LabelElementID := LowerCase(ElementID) + '.label';
    LabelText := GetLabelText(LabelElementID);
    
    // If label found, add required indicator (SVG)
    if LabelText <> '' then
      AppendRequiredIndicator(LabelElementID);
  end;
end;

/// <summary>
/// Marks all required field labels on a form with visual indicators.
/// Iterates through all controls on the form and calls MarkRequiredFieldLabel for each one.
/// Typically called during form initialization to show users which fields are required.
/// </summary>
/// <param name="aForm">The form containing controls to mark</param>
/// <param name="aEntityClassName">The entity class name to use for label lookups (e.g., 'TDonation')</param>
class procedure TRequiredFieldsUtils.MarkRequiredFieldLabels(aForm: TForm; const aEntityClassName: string);
var
  I: Integer;
begin
  // Loop through all controls on the form
  for I := 0 to aForm.ControlCount - 1 do
    MarkRequiredFieldLabel(aForm.Controls[I], aEntityClassName);
end;

/// <summary>
/// Updates the visual validity state of a control in the DOM (adds/removes CSS classes for valid/invalid styling).
/// Only updates controls that have Required=True. Uses TDocUtils.setControlValidity to apply CSS classes.
/// Checks if the control has a value and sets validity accordingly (vsValid if has value, vsInvalid if empty).
/// </summary>
/// <param name="AControl">The control whose validity state should be updated</param>
class procedure TRequiredFieldsUtils.UpdateControlValidity(AControl: TObject);
var
  ElementID: string;
  HasValue: Boolean;
begin
  // Get ElementID
  ElementID := '';
  GetPropertyValueAsString(AControl, 'ElementID', ElementID);
  
  if ElementID = '' then
    Exit;
  
  // Check if control has Required property set to True
  if IsControlRequired(TControl(AControl)) then
  begin
    // Check if control has a value
    HasValue := HasControlValue(AControl);
    
    // Set validity based on whether it has a value
    if HasValue then
      TDocUtils.setControlValidity(ElementID, vsValid)
    else
      TDocUtils.setControlValidity(ElementID, vsInvalid);
  end;
end;

/// <summary>
/// Sets up real-time validity checking for a single required field control.
/// Attaches JavaScript event listeners (change and input events) to the DOM element that will
/// automatically update the control's visual validity state as the user types or changes the value.
/// Handles different control types (select/combo boxes, text inputs, checkboxes, textareas) with
/// appropriate validation logic for each. Uses direct DOM manipulation to avoid RTTI event handler complexity.
/// Only sets up listeners for controls currently treated as required by IsControlRequired
/// (based on required-fields configuration and mappings).
/// </summary>
/// <param name="AControl">The control to set up for real-time validity checking</param>
class procedure TRequiredFieldsUtils.SetupRequiredFieldValidityForControl(AControl: TControl);
var
  ElementID: string;
begin
  // Check if control has Required property set to True
  if not IsControlRequired(AControl) then
    Exit;
  
  // Set initial validity
  UpdateControlValidity(AControl);
  
  // Get ElementID for attaching event listener
  ElementID := '';
  GetPropertyValueAsString(AControl, 'ElementID', ElementID);
  
  if ElementID <> '' then
  begin
    // Attach event listener directly to DOM element
    // This approach avoids RTTI event handler chaining complexity
    {$IFDEF PAS2JS}
    asm
      var element = document.getElementById(ElementID);
      if (element !== null) {
        // Create a function to check and update validity
        var checkValidity = function() {
          var hasValue = false;
          var tagName = element.tagName.toLowerCase();
          
          // Check if it's a select (combo box)
          if (tagName === 'select') {
            // For combo boxes, check if ItemIndex is -1 or if selected item is blank
            if (element.selectedIndex === -1) {
              hasValue = false;
            } else {
              // Check if the selected option's text/value is blank
              var selectedOption = element.options[element.selectedIndex];
              var optionText = selectedOption ? selectedOption.text.trim() : '';
              var optionValue = selectedOption ? selectedOption.value.trim() : '';
              // Invalid if both text and value are blank
              hasValue = (optionText !== '' || optionValue !== '');
            }
          } else if (tagName === 'input') {
            // For text inputs, check if value is not empty (trim whitespace)
            var inputType = element.type.toLowerCase();
            if (inputType === 'text' || inputType === 'date' || inputType === 'number') {
              var trimmedValue = element.value ? element.value.trim() : '';
              hasValue = trimmedValue !== '';
            } else if (inputType === 'checkbox') {
              hasValue = element.checked;
            }
          } else if (tagName === 'textarea') {
            // For textareas, check if value is not empty (trim whitespace)
            var trimmedValue = element.value ? element.value.trim() : '';
            hasValue = trimmedValue !== '';
          }
          
          // Update validity by manipulating CSS classes directly
          // This replicates what TDocUtils.setControlValidity does
          var validClasses = ['is-valid', 'is-invalid', 'is-void', 'is-none'];
          validClasses.forEach(function(cls) {
            element.classList.remove(cls);
          });
          
          if (hasValue) {
            element.classList.add('is-valid');
          } else {
            element.classList.add('is-invalid');
          }
        };
        
        // Attach change event listener
        element.addEventListener('change', checkValidity);
        
        // Also attach input event for text fields (updates as user types)
        if (element.tagName.toLowerCase() === 'input' && 
            (element.type === 'text' || element.type === 'date' || element.type === 'number')) {
          element.addEventListener('input', checkValidity);
        }
      }
    end;
    {$ENDIF}
  end;
end;

/// <summary>
/// Sets up real-time validity checking for all required fields on a form.
/// Iterates through all controls and calls SetupRequiredFieldValidityForControl for each one.
/// Typically called during form initialization to enable live validation feedback as users interact with fields.
/// </summary>
/// <param name="aForm">The form containing controls to set up</param>
class procedure TRequiredFieldsUtils.SetupRequiredFieldValidity(aForm: TForm);
var
  I: Integer;
begin
  // Loop through all controls on the form
  for I := 0 to aForm.ControlCount - 1 do
    SetupRequiredFieldValidityForControl(aForm.Controls[I]);
end;

/// <summary>
/// Updates the validity state of all required fields on a form.
/// Iterates through all controls and calls UpdateControlValidity for each required field.
/// Useful for refreshing validity states after programmatic changes to control values.
/// </summary>
/// <param name="aForm">The form containing controls to update</param>
class procedure TRequiredFieldsUtils.UpdateRequiredFieldsValidity(aForm: TForm);
var
  I: Integer;
  Ctrl: TControl;
begin
  // Loop through all controls and update validity for required fields
  for I := 0 to aForm.ControlCount - 1 do
  begin
    Ctrl := aForm.Controls[I];
    if IsControlRequired(Ctrl) then
      UpdateControlValidity(Ctrl);
  end;
end;

end.
